import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Play } from "lucide-react";
import { CARD, INPUT, SELECT } from "./adminStyles";

const PERSONALITIES = ["professional", "aggressive", "consultative"];

export const AdminAgentTab = ({ loading, onTrigger }) => {
  const [cfg, setCfg] = useState({ enabled: true, frequency: 60, personality: "professional", customMessage: "" });
  const set = (key) => (value) => setCfg((prev) => ({ ...prev, [key]: value }));

  return (
    <Card className={`${CARD} p-6`} data-testid="admin-agent-tab">
      <h2 className="text-xl font-bold text-white mb-6">Agent Behavior Controls</h2>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Label className="text-gray-400">Enable Autonomous Updates</Label>
          <Switch checked={cfg.enabled} onCheckedChange={set("enabled")} data-testid="agent-enabled-switch" />
        </div>
        <div>
          <Label className="text-gray-400">Activity Frequency (seconds)</Label>
          <div className="flex items-center gap-4 mt-2">
            <input type="range" min="10" max="300" value={cfg.frequency} onChange={(e) => set("frequency")(e.target.value)} className="flex-1" data-testid="agent-frequency-slider" />
            <span className="text-white font-semibold w-16">{cfg.frequency}s</span>
          </div>
        </div>
        <div>
          <Label className="text-gray-400">Agent Personality</Label>
          <select value={cfg.personality} onChange={(e) => set("personality")(e.target.value)} className={`w-full ${SELECT} mt-2`} data-testid="agent-personality-select">
            {PERSONALITIES.map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-gray-400">Custom Agent Message</Label>
          <Textarea value={cfg.customMessage} onChange={(e) => set("customMessage")(e.target.value)} placeholder="Enter a custom message for the agent to display..." className={`${INPUT} mt-2`} data-testid="agent-message-input" />
        </div>
        <Button onClick={onTrigger} disabled={loading} className="w-full bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="trigger-agent-btn">
          <Play className="h-4 w-4 mr-2" /> Trigger Agent Update Now
        </Button>
      </div>
    </Card>
  );
};

const ColorField = ({ label, value, onChange, testId }) => (
  <div>
    <Label className="text-gray-400">{label}</Label>
    <div className="flex gap-3 mt-2">
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="w-20 h-10 rounded cursor-pointer" data-testid={`${testId}-picker`} />
      <Input value={value} onChange={(e) => onChange(e.target.value)} className={INPUT} data-testid={`${testId}-input`} />
    </div>
  </div>
);

export const AdminUiTab = () => {
  const [cfg, setCfg] = useState({ primaryColor: "#00A38A", secondaryColor: "#00A38A", showLeaderboard: true, showAgentActions: true, logoUrl: "" });
  const set = (key) => (value) => setCfg((prev) => ({ ...prev, [key]: value }));

  return (
    <Card className={`${CARD} p-6`} data-testid="admin-ui-tab">
      <h2 className="text-xl font-bold text-white mb-6">UI Customization</h2>
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ColorField label="Primary Accent Color" value={cfg.primaryColor} onChange={set("primaryColor")} testId="ui-primary-color" />
          <ColorField label="Secondary Accent Color" value={cfg.secondaryColor} onChange={set("secondaryColor")} testId="ui-secondary-color" />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-gray-400">Show Peer Leaderboard</Label>
          <Switch checked={cfg.showLeaderboard} onCheckedChange={set("showLeaderboard")} data-testid="ui-leaderboard-switch" />
        </div>
        <div className="flex items-center justify-between">
          <Label className="text-gray-400">Show Agent Actions</Label>
          <Switch checked={cfg.showAgentActions} onCheckedChange={set("showAgentActions")} data-testid="ui-agent-actions-switch" />
        </div>
        <div>
          <Label className="text-gray-400">Custom Logo URL</Label>
          <Input value={cfg.logoUrl} onChange={(e) => set("logoUrl")(e.target.value)} placeholder="https://example.com/logo.png" className={`${INPUT} mt-2`} data-testid="ui-logo-input" />
        </div>
        <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/50">Note: UI changes require page refresh to take effect</Badge>
      </div>
    </Card>
  );
};
